import { map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Routes } from '../../config/routes';
import { JwtLegFiClaims } from '../auth/jwt-legfi-claims.model';
import { LegFiJwtService } from '../auth/legfi-jwt.service';
import { ApplicationHttpClient } from '../../components/shared/http/application-http-client';
import { LockboxDepositNotice } from '../../models/entities/lockbox-deposit-notice';
import { LockboxRejectDetail } from '../../models/entities/lockbox-reject-detail';
import { LockboxPostingDetail } from '../../models/entities/lockbox-posting-detail';
import { PaginationLinks, PaginationMeta } from '../../models/entities/pagination';

@Injectable({
    providedIn: 'root',
})
export class LockboxService
{
    private _http: ApplicationHttpClient;
    private cpiLockboxAddress = {
        line1: 'PO Box 981506',
        line2: '',
        city: 'West Sacramento',
        state: 'California',
        zip: '95798',
        countryCode: 'US'
    };
    private allianceLockboxAddresses = {
        '6546': {
            line1: 'PO Box 93723',
            line2: '',
            city: 'Las Vegas',
            state: 'Nevada',
            zip: '89193-3723',
            countryCode: 'US',
        },
        '7842': {
            line1: 'c/o Your HOA',
            line2: 'PO BOX 88296',
            city: 'Carol Stream',
            state: 'Illinois',
            zip: '60188',
            countryCode: 'US',
        },
        default: {
            line1: 'PO BOX 94046',
            line2: '',
            city: 'Las Vegas',
            state: 'Nevada',
            zip: '89193-4046',
            countryCode: 'US',
        },
    };

    /**
     * @param _http
     */
    constructor(_http: ApplicationHttpClient) {
        this._http = _http;
    }

    getCPILockboxAddress() {
        return this.cpiLockboxAddress;
    }

    getAllianceLockboxAddress(allianceLockboxPmcId: string) {
        return this.allianceLockboxAddresses[allianceLockboxPmcId] || this.allianceLockboxAddresses.default;
    }

    areRemittanceAndLockboxAddressesMatching(formAddress: any, lockboxAddress: any): boolean {
        return (
                (formAddress.line1 || '') === (lockboxAddress.line1 || '') &&
                (formAddress.line2 || '') === (lockboxAddress.line2 || '') &&
                (formAddress.city || '') === (lockboxAddress.city || '') &&
                (formAddress.state || '') === (lockboxAddress.state || '') &&
                (formAddress.zip || '') === (lockboxAddress.zip || '')
        );
    }

    /**
     * Mark a payment as returned, deleting it's associated payments
     * @param postingDetailId
     */
    public markPaymentReturned(postingDetailId: number): Observable<any> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.AdminLockboxPostingDetails(postingDetailId) + '/return',
        );

        return this._http.post(url, JSON.stringify({}));
    }

    /**
     * Trigger an immediate import of lockbox files
     */
    public importNow(): Observable<any> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.AdminLockbox() + '/import',
        );

        return this._http.post(url, JSON.stringify({}));
    }

    /**
     * Trigger an immediate push of the HOA list and validation file
     */
    public pushNow(): Observable<any> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.AdminLockbox() + '/push',
        );

        return this._http.post(url, JSON.stringify({}));
    }

    /**
     * Load all deposit notices
     */
    public getLockboxDepositNotices(
            search: string = '',
            sortColumn: string = 'name',
            sortDirection: string = 'desc',
            pageIndex: number = 1,
            perPage: number = 100,
            options: {} = {},
    ): Observable<LockboxDepositNoticesResponse> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        let url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.AdminLockboxDepositNotices(),
        );

        const params = [];
        params.push('page=' + pageIndex);
        params.push('search=' + encodeURIComponent(search));
        params.push('column=' + sortColumn);
        params.push('direction=' + sortDirection);
        params.push('perPage=' + perPage);

        url = url + '?' + params.join('&');

        return this._http.get(url).pipe(map((response) => new LockboxDepositNoticesResponse(response)));
    }

    /**
     * Load reject details for a particular deposit
     * @param depositNoticeId
     */
    public getRejectDetails(depositNoticeId: number): Observable<LockboxRejectDetail[]> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.AdminLockboxRejectDetails(depositNoticeId),
        );

        return this._http.get(url).pipe(map((response: Object[]) => {
            return response.map(item => new LockboxRejectDetail(item));
        }));
    }

    /**
     * Load posting details for a particular deposit
     * @param depositNoticeId
     */
    public getPostingDetails(depositNoticeId: number): Observable<LockboxPostingDetail[]> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.AdminLockboxPostingDetails(depositNoticeId),
        );

        return this._http.get(url).pipe(map((response: Object[]) => {
            return response.map(item => new LockboxPostingDetail(item));
        }));
    }

    /**
     * Load posting details for a particular deposit
     * @param depositNoticeId
     */
    public getPostingDetail(postingDetailId: number): Observable<LockboxPostingDetail> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.AdminLockboxPostingDetail(postingDetailId),
        );

        return this._http.get(url).pipe(map((response: Object) => {
            return new LockboxPostingDetail(response);
        }));
    }

    public savePostingDetail(postingDetailId: number, data: {}) {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.AdminLockboxPostingDetail(postingDetailId),
        );

        return this._http.patch(url, this._http.serializeObject(data)).pipe(map((response: Object) => {
            return new LockboxPostingDetail(response);
        }));
    }

    public reapplyDepositNotice(depositNoticeId: number) {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.AdminLockboxDepositNotices() + '/' + depositNoticeId + '/reapply',
        );

        return this._http.post(url, JSON.stringify({})).pipe(map((response: Object) => {
            return new LockboxDepositNotice(response);
        }));
    }

    /**
     * Load check details for a particular deposit
     * @param depositNoticeId
     */
    public getDepositDetails(depositNoticeId: number): Observable<LockboxPostingDetail[]> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.AdminLockboxDepositDetails(depositNoticeId),
        );

        return this._http.get(url).pipe(map((response: Object[]) => {
            return response.map(item => new LockboxPostingDetail(item));
        }));
    }

    /**
     * Delete a deposit notice and all associated payments
     * @param depositNoticeId
     */
    public deleteDepositNotice(depositNoticeId: number): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.AdminDeleteDepositNotice(depositNoticeId),
        );

        return this._http.delete(url);
    }

    public createDeposit(form: {
        organizationId: number,
        depositDate: string,
        checks: {
            checkNumber: string,
            amount: number,
            accountNumber: number,
            accountAmount: number,
        }[]
    }) {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.AdminLockboxDepositNotices(),
        );

        return this._http.post(url, JSON.stringify(form));
    }
}

export class LockboxDepositNoticesResponse
{
    data: LockboxDepositNotice[];
    links: PaginationLinks;
    meta: PaginationMeta;

    constructor(request: any) {
        this.data = (request.data && request.data.length) ? request.data.map(tx => {
            return new LockboxDepositNotice(tx);
        }) : [];
        this.links = new PaginationLinks(request.links);
        this.meta = new PaginationMeta(request.meta);
    }
}
